/**
 * SortableHeader — drop-in replacement for a plain <th> that handles
 * server-driven sorting on a list endpoint.
 *
 * Behaviour (matches the user's spec):
 *   • 1st click on a column     → asc
 *   • 2nd click on same column  → desc
 *   • 3rd click on same column  → clear (back to default sort)
 *   • Click on a different col  → asc on that column
 *   • Renders a tiny arrow (▲ / ▼) when the column is the active sort,
 *     a faded ↕ otherwise, so the operator always sees what's sortable.
 *   • Columns marked `sortable={false}` (the default) render as a plain
 *     header — used for Actions / icon / button columns.
 *
 * Wiring
 * ------
 *   <SortableHeader
 *       label="Amount"
 *       field="amount"
 *       sortBy={sortBy}
 *       sortDir={sortDir}
 *       onChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
 *       align="right"
 *   />
 *
 * The parent is responsible for putting `sortBy` + `sortDir` into the
 * URL query of its list fetch and resetting the page back to 1 when
 * the sort changes — same pattern as filter changes.
 */
import React from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

export default function SortableHeader({
  label,
  field,
  sortBy,
  sortDir,
  onChange,
  align = 'left',
  className = '',
  testid,
}) {
  const isActive = sortBy === field;
  const dir = isActive ? sortDir : null;

  const next = () => {
    if (!isActive) return onChange(field, 'asc');
    if (sortDir === 'asc') return onChange(field, 'desc');
    // 3rd click — clear the sort (back to endpoint default).
    return onChange('', '');
  };

  const alignCls =
    align === 'right'  ? 'justify-end'   :
    align === 'center' ? 'justify-center' :
                         'justify-start';
  const padCls =
    align === 'right'  ? 'pr-3 pl-3 text-right' :
    align === 'center' ? 'px-3 text-center'     :
                         'px-3 text-left';

  return (
    <th
      onClick={next}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); next(); } }}
      className={`py-3 ${padCls} cursor-pointer select-none hover:bg-white/10 transition-colors group ${className}`}
      data-testid={testid || `sort-th-${field}`}
      aria-sort={isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span className={`inline-flex items-center gap-1 ${alignCls}`}>
        <span>{label}</span>
        {/* Arrows inherit `currentColor` from the parent <th>, so they
            stay readable whether the header is the new cyan/blue gradient
            (parent text-white) or a legacy light-bg table (parent slate
            text). The inactive arrow is held back to 50 % opacity so
            it whispers rather than shouts. */}
        {isActive && dir === 'asc'  && <ArrowUp   className="w-3 h-3" data-testid={`sort-arrow-${field}-asc`} />}
        {isActive && dir === 'desc' && <ArrowDown className="w-3 h-3" data-testid={`sort-arrow-${field}-desc`} />}
        {!isActive && <ArrowUpDown className="w-3 h-3 opacity-50 group-hover:opacity-90" />}
      </span>
    </th>
  );
}

/**
 * useTableSort — tiny hook that owns the (sortBy, sortDir) state, the
 * onChange handler tables pass to <SortableHeader>, and the URL-param
 * helper for the GET request.
 *
 *   const { sortBy, sortDir, onSortChange, sortParams } = useTableSort();
 *   ...
 *   <SortableHeader label="..." field="..." sortBy={sortBy}
 *                   sortDir={sortDir} onChange={onSortChange} />
 *   ...
 *   const url = `/api/x?${baseParams}${sortParams()}`;
 *
 * Calling `onSortChange` also resets the current page to 1 (via the
 * onPageReset callback) — exactly the same pattern the existing
 * `onFilter` handlers already use.
 */
export function useTableSort({ onPageReset } = {}) {
  const [sortBy,  setSortBy]  = React.useState('');
  const [sortDir, setSortDir] = React.useState('');

  const onSortChange = React.useCallback((field, dir) => {
    setSortBy(field || '');
    setSortDir(dir || '');
    if (typeof onPageReset === 'function') onPageReset();
  }, [onPageReset]);

  const sortParams = React.useCallback(() => {
    if (!sortBy) return '';
    const usp = new URLSearchParams();
    usp.set('sort_by', sortBy);
    if (sortDir) usp.set('sort_dir', sortDir);
    return `&${usp.toString()}`;
  }, [sortBy, sortDir]);

  return { sortBy, sortDir, onSortChange, sortParams };
}
